'use client';

import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useCallback, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useLoginMutation, BasketQueryKey, NotesListQueryKey } from '@shared/api';
import { ENV } from '@shared/config';
import { AuthContext, Locale, setToken } from '@shared/libs';
import { Button, ButtonColor, Input } from '@shared/ui';

// eslint-disable-next-line boundaries/element-types
import { RecoveryPassBtn } from '../../../recovery-pass';
// eslint-disable-next-line boundaries/element-types
import { RegistrationBtn } from '../../../registration';
// eslint-disable-next-line import/order, import/no-internal-modules
import yaIcon from './assets/ya.svg';
// eslint-disable-next-line import/order, import/no-internal-modules
import googleIcon from './assets/google.png';
import styles from './login-form.module.css';

// eslint-disable-next-line import/no-internal-modules

interface Auth {
  login: string;
  password: string;
}

interface LoginFormProps {
  onClose?: () => void;
  locale: Locale;
  disableReg?: boolean;
  onAuthSuccess?: () => void;
}

export const LoginForm = ({ onClose, locale, disableReg, onAuthSuccess }: LoginFormProps) => {
  const { register, handleSubmit } = useForm<Auth>();

  const queryClient = useQueryClient();
  const { mutate, error, isError, isSuccess, data, status } = useLoginMutation();
  const { setIsAuth } = useContext(AuthContext);
  const router = useRouter();

  useEffect(() => {
    void (async () => {
      if (isSuccess && data) {
        await setToken(data);
        setIsAuth(true);
        
        if (onAuthSuccess) {
          onAuthSuccess();
        } else {
          router.push('/personal/account/notes/');
        }
        
        onClose?.();

        setTimeout(() => {
          void queryClient.invalidateQueries({
            queryKey: [BasketQueryKey],
          });
          void queryClient.invalidateQueries({
            queryKey: [NotesListQueryKey],
          });
        }, 300);
      }
    })();
  }, [isSuccess, data, status, setIsAuth, router, onClose, onAuthSuccess, queryClient]);

  useEffect(() => {
    const listener = async (event: { data?: { ACTION: string; token: string } }) => {
      if (event?.data?.ACTION === 'AUTH_SOCIAL' && event?.data?.token) {
        await setToken(event?.data?.token);
        setIsAuth(true);
        
        if (onAuthSuccess) {
          onAuthSuccess();
        } else {
          router.push('/personal/account/notes/');
        }
        
        onClose?.();
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    window.addEventListener('message', listener);

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      window.removeEventListener('message', listener);
    };
  }, [setIsAuth, router, onClose, onAuthSuccess]);

  const submit = useCallback(
    (form: Auth) => {
      mutate(form);
    },
    [mutate],
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form className={styles.form} onSubmit={handleSubmit(submit)}>
      <Input
        placeholder={locale.PAGE_PERSONAL_SIGN_IN_LOGIN_PH || 'PAGE_PERSONAL_SIGN_IN_LOGIN_PH'}
        label={locale.PAGE_PERSONAL_SIGN_IN_LOGIN || 'PAGE_PERSONAL_SIGN_IN_LOGIN'}
        formGroupClassName={styles.formGroup}
        className={styles.input}
        {...register('login', { required: true })}
      />
      <Input
        placeholder={locale.PAGE_PERSONAL_SIGN_IN_PASS_PH || 'PAGE_PERSONAL_SIGN_IN_PASS_PH'}
        label={locale.PAGE_PERSONAL_SIGN_IN_PASS || 'PAGE_PERSONAL_SIGN_IN_PASS'}
        type="password"
        autoComplete="on"
        className={styles.input}
        {...register('password', { required: true })}
      />
      <RecoveryPassBtn locale={locale} onCloseParentModal={onClose} />

      {isError && (
        <p
          className={styles.error}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html:
              error?.response?.data?.error?.msg ||
              locale.PAGE_PERSONAL_SIGN_IN_ERROR ||
              'PAGE_PERSONAL_SIGN_IN_ERROR',
          }}
        />
      )}

      <Button type="submit" color={ButtonColor.GREEN} className={styles.btn}>
        {locale.PAGE_PERSONAL_SIGN_IN_BTN || 'PAGE_PERSONAL_SIGN_IN_BTN'}
      </Button>
      {!disableReg && <RegistrationBtn locale={locale} onCloseParentModal={onClose} />}

      <div>
        <p className={styles.socialAuth}>
          <span>{locale.PAGE_PERSONAL_SIGN_SOCIAL_AUTH || 'PAGE_PERSONAL_SIGN_SOCIAL_AUTH'}</span>
        </p>
        <div className={styles.socialLinks}>
          {ENV.LANG === 'ru' && (
            <Image
              className={styles.socialLinks_btn}
              src={yaIcon}
              width={36}
              height={36}
              alt="yandex"
              onClick={() => {
                window.open(
                  `https://oauth.yandex.ru/authorize?response_type=code&client_id=${process.env.NEXT_PUBLIC_YA_OAUTH_CLIENT_ID}`,
                  'new_window',
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  {
                    left: 200,
                    width: 200,
                    menubar: 'no',
                  },
                );
              }}
            />
          )}
          <Image
            className={styles.socialLinks_btn}
            src={googleIcon}
            width={36}
            height={36}
            alt="google"
            onClick={() => {
              window.open(
                `${process.env.NEXT_PUBLIC_GOOGLE_AUTH}${process.env.NEXT_PUBLIC_GOOGLE_AUTH_REDIRECT_URI}`,
                'new_window',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                {
                  left: 200,
                  width: 200,
                  menubar: 'no',
                },
              );
            }}
          />
        </div>
      </div>
    </form>
  );
};
